/**
 * Register.jsx — Gmail-only public registration.
 *
 * GMAIL-ONLY-AUTH (2026-02-27)
 *
 * Two-step premium 2-column layout:
 *   STEP 1 — "Continue with Gmail" (GoogleSignInButton). On success the
 *            client receives a verified ID Token + identity claims;
 *            email is ALREADY verified by Google — no Email OTP is
 *            generated.
 *   STEP 2 — Company details + UAE pricing snapshot. Phone is OPTIONAL
 *            (user may skip it entirely). Submit posts to
 *            POST /api/public/auth/google-register, which the backend
 *            re-validates against the live UAE pricing matrix and the
 *            originally-signed Google ID Token.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
 * THIS BREAKS THE AUTH.
 */
import { useEffect, useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { usePlatformBranding, brandUrl } from "@/context/PlatformBrandingContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import GoogleSignInButton from "@/components/auth/GoogleSignInButton";
import { useGoogleAuthConfig } from "@/context/GoogleAuthProvider";
import UaePricingPicker from "@/components/registration/UaePricingPicker";
import { ShieldCheck, Sparkles, Lock, Globe2, CheckCircle2 } from "lucide-react";

const DEFAULT_LOGO = "/logo.svg";

export default function Register() {
  const { user, refresh } = useAuth();
  const googleCfg = useGoogleAuthConfig();
  const nav = useNavigate();
  const loc = useLocation();
  const qs = new URLSearchParams(loc.search);
  const presetBundle = qs.get("module_bundle") || "certification";
  const presetCycle  = qs.get("billing_cycle") === "annual" ? "annual" : "monthly";
  const presetPlanVersionId  = qs.get("plan_version_id")  || null;
  const presetPricingOfferId = qs.get("pricing_offer_id") || null;

  // STEP 1 — Google identity (verified by backend)
  const [googleCredential, setGoogleCredential] = useState(null);
  const [googleIdentity, setGoogleIdentity] = useState(null); // {email, name, picture}

  // STEP 2 — Company + optional phone
  const [countries, setCountries] = useState([]);
  const [form, setForm] = useState({
    company_legal_name: "",
    trade_name: "",
    country: "",
    phone: "",
    preferred_workspace_slug: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const [uaeSelection, setUaeSelection] = useState({
    module_bundle: presetBundle, billing_cycle: presetCycle,
    plan_version_id: presetPlanVersionId, pricing_offer_id: presetPricingOfferId,
  });
  const [uaeSnapshot, setUaeSnapshot] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/public/countries");
        setCountries(data?.data || []);
      } catch { /* keep dropdown empty if backend unreachable */ }
    })();
  }, []);

  if (user) {
    return <Navigate to="/subscription/change-plan" replace />;
  }

  const onField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // Decode the ID token client-side ONLY for display purposes (name +
  // picture). The backend ALWAYS re-validates the signed JWT itself.
  const onGoogleCredential = (credential) => {
    setGoogleCredential(credential);
    try {
      const payload = JSON.parse(atob(credential.split(".")[1]));
      setGoogleIdentity({
        email: payload.email,
        name: payload.name || `${payload.given_name || ""} ${payload.family_name || ""}`.trim(),
        picture: payload.picture || "",
      });
    } catch {
      setGoogleIdentity({ email: "", name: "", picture: "" });
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!googleCredential || !googleIdentity) {
      toast.error("Please sign in with Google first.");
      return;
    }
    if (!uaeSnapshot || !uaeSnapshot.plan_version_id || !uaeSnapshot.pricing_offer_id) {
      toast.error("Please choose a subscription plan first.");
      return;
    }
    if (!form.company_legal_name) {
      toast.error("Please enter your company name.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/public/auth/google-register", {
        credential: googleCredential,
        company_legal_name: form.company_legal_name.trim(),
        trade_name: form.trade_name.trim() || undefined,
        country: form.country.trim() || undefined,
        phone: form.phone.trim() || undefined,
        preferred_workspace_slug: form.preferred_workspace_slug.trim() || undefined,
        billing_cycle: uaeSnapshot.billing_cycle,
        // Plan Studio canonical (2026-07-11) — the ONLY authoritative
        // commercial identifiers sent to the backend. Backend re-loads
        // both server-side and derives modules/features/limits.
        plan_version_id:  uaeSnapshot.plan_version_id,
        pricing_offer_id: uaeSnapshot.pricing_offer_id,
      });
      const next = data?.data?.next_step;
      const pb = data?.data?.subscription?.purchase_behavior;
      await refresh().catch(() => {});
      if (next === "checkout" || pb === "paid_checkout") {
        // Tenant created — direct the operator to the platform-managed
        // change-plan / checkout page which uses canonical IDs.
        toast.success("Workspace created. Complete billing when you're ready.");
        nav("/subscription/change-plan");
        return;
      }
      toast.success("Welcome to CertOpz.");
      nav("/");
    } catch (err) {
      const detail = err?.response?.data?.errors?.[0];
      if (detail?.code === "PRICING_MATRIX_CHANGED") {
        toast.error("Pricing has been updated. Please reload and choose your plan again.");
        setTimeout(() => window.location.reload(), 1500);
        return;
      }
      if (detail?.code === "GOOGLE_ACCOUNT_LINK") {
        toast.message("This Gmail is already registered. Redirecting to sign-in…");
        nav("/login");
        return;
      }
      if (detail?.code === "GOOGLE_AUTH_INVALID") {
        toast.error("Your Google session expired. Please click Continue with Gmail again.");
        setGoogleCredential(null);
        setGoogleIdentity(null);
        return;
      }
      toast.error(detail?.message || "Registration failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" data-testid="register-screen">
      <RegisterHeader />

      <main className="max-w-7xl mx-auto px-6 py-10 grid grid-cols-1 lg:grid-cols-3 gap-8" data-testid="register-grid">
        {/* Left — registration */}
        <form onSubmit={onSubmit} className="lg:col-span-2 space-y-6" data-testid="register-form">
          <div>
            <span className="text-[10px] tracking-[0.3em] text-teal-700 font-bold uppercase">Create account</span>
            <h1 className="mt-1 text-3xl sm:text-4xl font-bold text-slate-900 leading-tight">Register your CertOpz workspace</h1>
            <p className="mt-2 text-sm text-slate-600">
              Already have an account?{" "}
              <Link to="/login" className="text-cyan-700 font-semibold hover:underline" data-testid="register-login-link">Sign in</Link>
            </p>
          </div>

          <div className="lg:hidden">
            <UaePricingPicker
              value={uaeSelection}
              onChange={(sel, snap) => {
                setUaeSelection(sel);
                if (snap) setUaeSnapshot(snap);
              }}
            />
          </div>

          {/* STEP 1 — Google sign-in */}
          <fieldset className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm space-y-4" data-testid="register-step-google">
            <div className="flex items-center gap-2">
              <span className="w-5 h-5 rounded-full bg-teal-600 text-white text-[10px] font-bold flex items-center justify-center">1</span>
              <span className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Sign in with Google</span>
            </div>

            {!googleIdentity ? (
              <>
                <p className="text-sm text-slate-600 leading-relaxed">
                  Your Google account securely verifies your email address.{" "}
                  <b className="text-slate-900">No email verification code is required.</b>
                </p>
                <GoogleSignInButton onCredential={onGoogleCredential} label="continue_with" />
                {!googleCfg.enabled ? (
                  <div className="text-[11px] text-slate-500">
                    Operator setup pending. Existing users can still sign in via{" "}
                    <Link to="/login" className="text-cyan-700 font-semibold underline">Internal login</Link>.
                  </div>
                ) : null}
              </>
            ) : (
              <div className="flex items-center gap-3 rounded-lg bg-emerald-50 border border-emerald-200 p-3" data-testid="register-google-identity">
                {googleIdentity.picture ? (
                  <img src={googleIdentity.picture} alt="" className="w-9 h-9 rounded-full" />
                ) : (
                  <div className="w-9 h-9 rounded-full bg-emerald-200 flex items-center justify-center font-bold text-emerald-800">
                    {(googleIdentity.email || "?")[0].toUpperCase()}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-slate-900 truncate">{googleIdentity.name || googleIdentity.email}</div>
                  <div className="text-[11px] text-slate-600 truncate flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                    {googleIdentity.email} · Google-verified
                  </div>
                </div>
                <button type="button" onClick={() => { setGoogleCredential(null); setGoogleIdentity(null); }}
                        className="text-[11px] text-slate-500 hover:text-slate-900 underline"
                        data-testid="register-google-switch">
                  Switch
                </button>
              </div>
            )}
          </fieldset>

          {/* STEP 2 — Company details (enabled only once Google identity is captured) */}
          <fieldset
            className={`rounded-2xl border bg-white p-5 shadow-sm space-y-4 transition ${googleIdentity ? "border-slate-200" : "border-slate-200 opacity-60 pointer-events-none"}`}
            data-testid="register-step-company"
          >
            <div className="flex items-center gap-2">
              <span className="w-5 h-5 rounded-full bg-teal-600 text-white text-[10px] font-bold flex items-center justify-center">2</span>
              <span className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Company details</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="company_legal_name">Company legal name *</Label>
                <Input id="company_legal_name" required data-testid="register-company-name"
                  value={form.company_legal_name} onChange={onField("company_legal_name")} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="trade_name">Trade name</Label>
                <Input id="trade_name" data-testid="register-trade-name"
                  value={form.trade_name} onChange={onField("trade_name")} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="country">Country</Label>
                <select id="country" data-testid="register-country"
                  value={form.country}
                  onChange={(e) => setForm({ ...form, country: e.target.value })}
                  className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white">
                  <option value="">— select —</option>
                  {countries.map((c) => (
                    <option key={c.code || c.name} value={c.name}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="phone">
                  Mobile / Cell phone <span className="text-slate-400 font-normal">(optional)</span>
                </Label>
                <Input id="phone" placeholder="+971501234567" data-testid="register-phone"
                  value={form.phone} onChange={onField("phone")} />
                <div className="text-[11px] text-slate-500">
                  Phone verification is optional. You can add and verify your number later from workspace settings.
                </div>
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="preferred_workspace_slug">Preferred workspace subdomain (optional)</Label>
                <Input id="preferred_workspace_slug" placeholder="acme" data-testid="register-slug"
                  value={form.preferred_workspace_slug} onChange={onField("preferred_workspace_slug")} />
                <div className="text-[11px] text-slate-500">
                  Becomes {form.preferred_workspace_slug || "yourcompany"}.certopz.com once your account is active.
                </div>
              </div>
            </div>
          </fieldset>

          <Button
            type="submit"
            disabled={submitting || !googleIdentity || !uaeSnapshot?.pricing_offer_id}
            className="w-full h-12 text-base font-semibold"
            style={{ background: "#0F766E" }}
            data-testid="register-submit"
          >
            {submitting ? "Creating account…" : "Create account & continue to checkout"}
          </Button>
          <div className="text-[11px] text-slate-500 text-center">
            By creating an account you agree to the CertOpz terms of service. Pricing is locked to the displayed UAE matrix; tampered submissions are rejected with 409.
          </div>
        </form>

        {/* Right — sticky UAE pricing picker. */}
        <aside className="hidden lg:block lg:col-span-1">
          <div className="sticky top-6 space-y-5">
            <UaePricingPicker
              value={uaeSelection}
              onChange={(sel, snap) => {
                setUaeSelection(sel);
                if (snap) setUaeSnapshot(snap);
              }}
            />
            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-2">Why CertOpz</div>
              <ul className="space-y-2 text-[12px] text-slate-700">
                <li className="flex items-center gap-2"><ShieldCheck className="w-3.5 h-3.5 text-teal-600" /> Gmail sign-in · no password to remember</li>
                <li className="flex items-center gap-2"><Lock className="w-3.5 h-3.5 text-teal-600" /> 256-bit encryption · audit logs · RBAC</li>
                <li className="flex items-center gap-2"><Globe2 className="w-3.5 h-3.5 text-teal-600" /> UAE-ready · AED billing · VAT itemized</li>
                <li className="flex items-center gap-2"><Sparkles className="w-3.5 h-3.5 text-teal-600" /> Cancel any time · upgrade modules later</li>
              </ul>
            </div>
          </div>
        </aside>
      </main>
    </div>
  );
}

function RegisterHeader() {
  // Register is a pre-auth public surface — must display platform
  // identity only (2026-08-06 branding boundary). Consumes the platform
  // `website` asset; the bundled /logo.svg is the packaged fallback.
  // Tenant branding is intentionally NOT resolved here.
  const platformBranding = usePlatformBranding();
  const websiteLogo = brandUrl(platformBranding?.website) || DEFAULT_LOGO;
  const displayName = "CertOpz";
  return (
    <header className="border-b border-slate-200 bg-white/95 backdrop-blur sticky top-0 z-10">
      <div className="max-w-7xl mx-auto px-6 py-3.5 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5">
          <div
            className="rounded-lg p-1.5 border border-slate-200 shadow-sm flex items-center justify-center"
            style={{ minWidth: 36, height: 36, backgroundColor: "#ffffff" }}
          >
            <img
              src={websiteLogo}
              onError={(e) => { if (e.currentTarget.src !== DEFAULT_LOGO) e.currentTarget.src = DEFAULT_LOGO; }}
              alt={displayName}
              className="object-contain"
              style={{ width: "auto", height: "auto", maxWidth: 140, maxHeight: 26 }}
            />
          </div>
          <div className="leading-tight">
            <div className="text-base font-bold text-slate-900 tracking-tight" style={{ fontFamily: "Plus Jakarta Sans" }}>{displayName}</div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-teal-700">Register</div>
          </div>
        </Link>
        <Link to="/pricing" className="text-sm font-semibold text-slate-700 hover:text-slate-900">View pricing →</Link>
      </div>
    </header>
  );
}
